import React, { useEffect, useState, useCallback } from "react";
import { api, fmtMoney, fmtMoneyShort } from "@/lib/caesar";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, EmptyState, SectionLabel, MetricCard, Labeled } from "@/components/common";
import { MiniBar, Funnel } from "@/components/charts";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Check, Clock } from "lucide-react";

const NUM = [
  ["phone_calls", "Phone Calls"], ["emails_sent", "Emails Sent"], ["dms_sent", "WhatsApp / DMs"],
  ["follow_ups", "Follow-Ups"], ["new_leads_contacted", "Leads Contacted"], ["new_leads_generated", "New Leads"],
  ["sales_conversations", "Sales Convos"], ["appointments_booked", "Appointments"], ["proposals_sent", "Proposals"],
  ["sales_closed", "Sales Closed"], ["money_collected", "Money Collected ($)"], ["social_posts", "Social Posts"],
  ["videos_created", "Videos"], ["clients_served", "Clients Served"], ["tasks_completed", "Tasks Done"],
];
const TXT = [["biggest_win", "Biggest win today"], ["biggest_problem", "Biggest problem / blocker"], ["priorities_tomorrow", "Top 3 priorities tomorrow"], ["other_info", "Other important info"]];

export default function EOD() {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  return (
    <div className="space-y-8">
      <PageHeader title="EOD Report" subtitle="End-of-day execution — 2 minutes, every day" testId="eod-header" />
      {isAdmin && <TodaysExecution />}
      {isAdmin && <WeeklyScorecard />}
      <SubmitForm />
    </div>
  );
}

function WeeklyScorecard() {
  const [range, setRange] = useState("this_week");
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/dashboard/weekly?range=${range}`).then(({ data }) => setD(data)).catch(() => {}); }, [range]);
  const RANGES = [["this_week", "This Week"], ["last_week", "Last Week"], ["this_month", "This Month"]];
  const ranked = d ? [...d.per_person].sort((a, b) => (b.money_collected - a.money_collected) || (b.sales_closed - a.sales_closed) || (b.phone_calls - a.phone_calls)) : [];
  const any = ranked.some((p) => p.phone_calls || p.sales_closed || p.money_collected || p.emails_sent || p.dms_sent);
  return (
    <div>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <SectionLabel className="!mb-0">Weekly Team Scorecard</SectionLabel>
        <div className="flex gap-2">
          {RANGES.map(([k, l]) => (
            <button key={k} data-testid={`scorecard-range-${k}`} onClick={() => setRange(k)} className={"px-3 py-1.5 rounded-full text-xs font-semibold border " + (range === k ? "bg-gold text-ink border-gold" : "border-edge text-[#A3A3A3]")}>{l}</button>
          ))}
        </div>
      </div>
      {!d || !any ? <EmptyState testId="scorecard-empty" title="No EOD data for this range yet." hint="Rankings appear once reports are submitted." /> : (
        <div className="bg-surface border border-edge rounded-xl overflow-hidden">
          <div className="grid grid-cols-6 gap-2 px-4 py-2.5 border-b border-edge label-caps text-[0.55rem]">
            <span className="col-span-2">Member</span><span className="text-right">Calls</span><span className="text-right">Leads</span><span className="text-right">Sales</span><span className="text-right">Revenue</span>
          </div>
          {ranked.map((p, i) => (
            <div key={p.name} data-testid={`scorecard-row-${i}`} className="grid grid-cols-6 gap-2 px-4 py-3 border-b border-edge/50 last:border-0 items-center">
              <span className="col-span-2 font-semibold text-sm flex items-center gap-2"><span className="font-num text-gold/50 w-4">{i + 1}</span>{p.name}</span>
              <span className="text-right font-num text-sm">{p.phone_calls}</span>
              <span className="text-right font-num text-sm">{p.new_leads_generated}</span>
              <span className="text-right font-num text-sm text-sgreen">{p.sales_closed}</span>
              <span className="text-right font-num text-sm text-sgreen">{fmtMoneyShort(p.money_collected)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SubmitForm() {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/eod/mine").then(({ data }) => setForm(data || {})).catch(() => {}); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    setBusy(true);
    try { await api.post("/eod", form); toast.success("EOD submitted"); } catch { toast.error("Failed"); } finally { setBusy(false); }
  };
  return (
    <div className="bg-surface border border-edge rounded-xl p-5">
      <SectionLabel>Submit Today's Report</SectionLabel>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {NUM.map(([k, label]) => (
          <Labeled key={k} label={label}>
            <Input data-testid={`eod-${k}`} type="number" value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} className="bg-ink border-edge font-num" placeholder="0" />
          </Labeled>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-3 mt-4">
        {TXT.map(([k, label]) => (
          <Labeled key={k} label={label}><Textarea value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} className="bg-ink border-edge min-h-[60px]" /></Labeled>
        ))}
      </div>
      <button data-testid="eod-submit" onClick={submit} disabled={busy} className="mt-4 w-full sm:w-auto px-6 h-11 rounded-lg bg-gold text-ink font-bold hover:bg-[#e6c14e] transition-colors">
        {busy ? "…" : "Submit EOD Report"}
      </button>
    </div>
  );
}

function TodaysExecution() {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/dashboard/execution").then(({ data }) => setD(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Loading />;
  const t = d.team;
  const anyData = d.per_person.some((p) => p.submitted);
  const funnel = [
    { label: "Leads", value: d.funnel.leads }, { label: "Contacted", value: d.funnel.contacted },
    { label: "Appointments", value: d.funnel.appointments }, { label: "Proposals", value: d.funnel.proposals }, { label: "Sales", value: d.funnel.sales },
  ];
  return (
    <div className="space-y-6">
      <div>
        <SectionLabel>Today's Execution — Team</SectionLabel>
        {!anyData ? <EmptyState testId="eod-empty" title="No EOD reports yet today." hint="Team analytics appear after the first reports are submitted." /> : (
          <>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
              <MetricCard label="Calls" value={t.phone_calls} />
              <MetricCard label="Emails" value={t.emails_sent} />
              <MetricCard label="DMs" value={t.dms_sent} />
              <MetricCard label="Leads" value={t.new_leads_generated} />
              <MetricCard label="Sales" value={t.sales_closed} accent="#10B981" />
              <MetricCard label="Revenue" value={fmtMoneyShort(t.money_collected)} accent="#10B981" />
            </div>
            <div className="grid sm:grid-cols-3 gap-3 mt-3">
              {d.per_person.map((p) => (
                <div key={p.user_id} data-testid={`exec-person-${p.user_id}`} className="bg-surface border border-edge rounded-xl p-4">
                  <div className="flex items-center justify-between"><span className="font-semibold">{p.name}</span>{p.submitted ? <Check size={15} className="text-sgreen" /> : <Clock size={15} className="text-samber" />}</div>
                  <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                    {[["Calls", p.phone_calls], ["DMs", p.dms_sent], ["Leads", p.new_leads_generated], ["Appts", p.appointments_booked], ["Sales", p.sales_closed], ["$", fmtMoneyShort(p.money_collected)]].map(([l, v]) => (
                      <div key={l}><div className="font-num text-lg">{v}</div><div className="label-caps text-[0.5rem]">{l}</div></div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-surface border border-edge rounded-xl p-5"><SectionLabel>7-Day Team Activity</SectionLabel><MiniBar data={d.seven_day} dataKey="activity" /></div>
        <div className="bg-surface border border-edge rounded-xl p-5"><SectionLabel>Sales Conversion (This Week)</SectionLabel><Funnel steps={funnel} /></div>
      </div>
    </div>
  );
}
